import React, {
  useCallback
} from "react";
import {
  styled,
  Box
} from "@mui/material";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  margin,
  padding
} from "theme/defaultStyle";
import FileUploadIcon from "theme/icons/FileUploadIcon";
import {
  ErrorCode,
  FileRejection,
  FileWithPath,
  useDropzone
} from "react-dropzone";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { DocumentType, UIConstants } from "utils/data/enum";
import { showToast } from "../stewart-toast/showToast";

const Container = styled(Box)<{ isSignature: boolean }>(({ isSignature }) => ({
  "& .drop-zone": {
    marginTop: margin.xlarge,
    height: "201px",
    border: `1px dashed ${colors.blue19}`,
    borderRadius: borderRadius.small,
    background: "#92C4DB1A",
    minWidth: "100%",
    display: "flex",
  },
  "& .drop-zone-text": {
    margin: "auto",
    textAlign: "center",
    height: "129px",
    // width: "700px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingLeft: padding.small2,
    paddingRight: padding.small2,
  },
  "& .drop-zone-text .file-upload-icon": {
    margin: "auto",
  },
  "& .drop-zone-text .drag-and-drop": {
    fontWeight: fontWeight.normal2,
    fontSize: fontSize.large,
  },
  "& .drop-zone-text .choose-files": {
    fontWeight: fontWeight.normal2,
    fontSize: fontSize.large,
    color: colors.blue01,
    textDecoration: isSignature ? "none" : "underline",
  },
  "& .drop-zone-text .supported-formats": {
    fontWeight: fontWeight.normal1,
    fontSize: fontSize.small,
  },
  ...(isSignature && {
    "& .drop-zone-text .supported-formats .label": {
      color: colors.blue18,
    },
  }),
}));

type Props = {
  disabled?: boolean;
  documentType?: string;
  handleOnUpload: (acceptedFiles:FileWithPath[], fileRejections: FileRejection[]) => void;
}

const UploadDropZone = ({disabled = false, documentType = DocumentType.POLICY, handleOnUpload}:Props) => {
  const { generalConfig } = useConfigContext();

  const allowedFileSize = generalConfig?.policyUploadConfiguration?.allowedFileSize ? generalConfig?.policyUploadConfiguration?.allowedFileSize : 50;
  const maxFileSize = (allowedFileSize * 1024 * 1024);

  function maxFileSizeValidator(file: FileWithPath) {
    // Signature-specific size validation (1 MB)
    if (documentType === DocumentType.SIGNATURE) {
      const signatureMaxFileSize = 1 * 1024 * 1024; // 1 MB
  
      if (file.size > signatureMaxFileSize) {
        return {
          code: ErrorCode.FileTooLarge,
          message: UIConstants.FILE_SIZE_EXCEEDED_ERROR_MESSAGE.toString(),
        };
      }
    } 
    else if (file.size > maxFileSize) {
      return {
        code: ErrorCode.FileTooLarge,
        message: "File size is too large",
      };
    }
  
    if (file.size <= 0) {
      return {
        code: ErrorCode.FileTooSmall,
        message: "File size is too small",
      };
    }
  
    return null;
  }
  
  const getAcceptMimeTypes = () => {
    const accept = new Map();
    switch (documentType) {
      case DocumentType.POLICY:
        if (generalConfig?.policyUploadConfiguration?.acceptMimeTypes &&
          generalConfig?.policyUploadConfiguration?.acceptMimeTypes.length > 0
        ) {
          generalConfig.policyUploadConfiguration.acceptMimeTypes.map((acceptMimeType) => {
            const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
            accept.set(acceptMimeType.mimeType,extensions);
          })
        }
        break;
      case DocumentType.TITLEDOCS:
      case DocumentType.TITLESEARCHDOC:
      case DocumentType.TITLESEARCHDATA:
        if (generalConfig?.titleSearchUploadConfiguration?.acceptMimeTypes &&
          generalConfig?.titleSearchUploadConfiguration?.acceptMimeTypes.length > 0
        ) {
          generalConfig.titleSearchUploadConfiguration?.acceptMimeTypes.map((acceptMimeType) => {
            const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
            accept.set(acceptMimeType.mimeType,extensions);
          })
        }
        break;
      //Document type file is used for UWA
      case DocumentType.FILE:
        if (generalConfig?.uwaUploadConfiguration?.acceptMimeTypes &&
          generalConfig?.uwaUploadConfiguration?.acceptMimeTypes.length > 0
        ) {
          generalConfig.uwaUploadConfiguration?.acceptMimeTypes.map((acceptMimeType) => {
            const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
            accept.set(acceptMimeType.mimeType,extensions);
          })
        }
        break;
      case DocumentType.SIGNATURE:
        accept.set('image/png', ['.png']);
        accept.set('image/jpeg', ['.jpeg', '.jpg']);
        break;
      default:
        accept.set('application/pdf', ['.pdf']);
        break;
    }
    // if (generalConfig?.policyUploadConfiguration?.acceptMimeTypes && 
    //   generalConfig?.policyUploadConfiguration?.acceptMimeTypes.length > 0
    // ) {
    //   generalConfig.policyUploadConfiguration.acceptMimeTypes.map((acceptMimeType) => {
    //     const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
    //     accept.set(acceptMimeType.mimeType,extensions);
    //   })
    // }
    // else {
    //   accept.set('application/pdf',['.pdf']);
    // }
    // console.log('accept:', accept);
    return accept;
  }

  const getSupportedExtensions = () => {
    let supportedExtensions = "";
    switch (documentType) {
      case DocumentType.POLICY:
        if (generalConfig?.policyUploadConfiguration?.acceptMimeTypes &&
          generalConfig?.policyUploadConfiguration?.acceptMimeTypes.length > 0
        ) {
          generalConfig.policyUploadConfiguration.acceptMimeTypes.map((acceptMimeType) => {
            // const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
            const supportedExtension = acceptMimeType.supportedExtension;
            if (supportedExtensions.length > 0) {
              supportedExtensions += ", ";
            }
            supportedExtensions += supportedExtension; // extensions.join(", ");
          });        }
        break;

      case DocumentType.TITLEDOCS:
      case DocumentType.TITLESEARCHDOC:
      case DocumentType.TITLESEARCHDATA:
        if (generalConfig?.titleSearchUploadConfiguration?.acceptMimeTypes &&
          generalConfig?.titleSearchUploadConfiguration?.acceptMimeTypes.length > 0
        ) {
          generalConfig.titleSearchUploadConfiguration.acceptMimeTypes.map((acceptMimeType) => {
            // const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
            const supportedExtension = acceptMimeType.supportedExtension;
            if (supportedExtensions.length > 0) {
              supportedExtensions += ", ";
            }
            supportedExtensions += supportedExtension; // extensions.join(", ");
          })
        }
        break;
      //Document type file is used for UWA
      case DocumentType.FILE:
        if (generalConfig?.uwaUploadConfiguration?.acceptMimeTypes &&
          generalConfig?.uwaUploadConfiguration?.acceptMimeTypes.length > 0
        ) {
          generalConfig.uwaUploadConfiguration.acceptMimeTypes.map((acceptMimeType) => {
            // const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
            const supportedExtension = acceptMimeType.supportedExtension;
            if (supportedExtensions.length > 0) {
              supportedExtensions += ", ";
            }
            supportedExtensions += supportedExtension; // extensions.join(", ");
          })
        }
        break;
      case DocumentType.SIGNATURE:
        supportedExtensions = ".jpg, .jpeg, .png,";
        break;
      default:
        supportedExtensions = ".pdf";
        break;
    }

    // if (generalConfig?.policyUploadConfiguration?.acceptMimeTypes && 
    //   generalConfig?.policyUploadConfiguration?.acceptMimeTypes.length > 0
    // ) {

    //   generalConfig.policyUploadConfiguration.acceptMimeTypes.map((acceptMimeType) => {
    //     const extensions = acceptMimeType.extensions.map((extension) => extension.extension);
    //     if (supportedExtensions.length > 0) {
    //       supportedExtensions += ", ";
    //     }
    //     supportedExtensions += extensions.join(", ");
    //   })
    // }
    // else {
    //   supportedExtensions = ".pdf";
    // }
    return supportedExtensions;
  }

  // console.log('accept:', getAcceptMimeTypes());
  // console.log('accept object:', Object.fromEntries(getAcceptMimeTypes()));
  // console.log('getSupportedExtensions:', getSupportedExtensions());
  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
  
      if (documentType === DocumentType.SIGNATURE && fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          const fileName = rejection.file.name;
  
          rejection.errors.forEach((error) => {
            let message = "";
  
            if (error.code === ErrorCode.FileInvalidType) {
              message = `Stewart Connect could not open ${fileName} because it is either not a supported file type or because the file has been damaged. The image must be in one of the following formats: JPG, JPEG or PNG.`;
            }
  
            if (error.code === ErrorCode.FileTooLarge) {
              message = `Stewart Connect could not upload ${fileName} because it exceeds the maximum allowed size of 1 MB.`;
            }
  
            if (error.code === ErrorCode.FileTooSmall) {
              message = `Stewart Connect could not upload ${fileName} because the file is empty or corrupted.`;
            }
  
            if (message) {
              showToast({
                type: "error",
                title: message,
              });
            }
          });
        });
  
        return;
      }
  
      await handleOnUpload(acceptedFiles, fileRejections);
    },
    [documentType]
  );
  

  // TODO - will be updated based onfiguration
  const { getRootProps, getInputProps  } = useDropzone({
    // accept: { 
    //   // 'image/png': ['.png'],
    //   // 'image/jpeg': ['.jpeg', '.jpg'],
    //   'application/pdf': ['.pdf'],
    //   // 'application/msword': ['.doc'],
    //   // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    // },    
    accept: Object.fromEntries(getAcceptMimeTypes()),
    validator: maxFileSizeValidator,
    onDrop,
    disabled: disabled,
  });

  return (
    <>
      <Container isSignature={documentType === DocumentType.SIGNATURE}>
        <div className="drop-zone" {...getRootProps()}>
          <input {...getInputProps()} />
          <div className="drop-zone-text">
            <div className="file-upload-icon">
              <FileUploadIcon />
            </div>
            <div className="drag-and-drop">
              Drag and drop or&nbsp;
              <span className="choose-files">choose files</span>
            </div>
            <div className="supported-formats">
              <span className="label">Supported formats: {getSupportedExtensions()} with a maximum size of {documentType === DocumentType.SIGNATURE ? 1 : allowedFileSize} MB per file</span>
            </div>

          </div>
        </div>
      </Container>
    </>
  )

}

export default UploadDropZone;